import { useEffect } from "react";
import { CardTitle, Col, Form, FormGroup, Row } from "react-bootstrap";
import { compose } from "redux";
import { withTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { Controller, useForm } from "react-hook-form";
import classNames from "classnames";
import { ErrorMessage } from "@hookform/error-message";
import Toggle from "react-toggle";
import Select from "react-select";
import { useIntl } from "react-intl";
import {
  Card,
  CardBody,
  CardHeader,
} from "../../../../_metronic/_partials/controls";
import {
  useCreateInvestorSubscription,
  useUpdateInvestorSubscription,
  useGetSubscriptionPlans,
} from "../../../../swrHelpers/settings/revenueSettings";
import { useSelector } from "react-redux";

const AddEditInvestorSubscription = () => {
  const intl = useIntl();
  const navigate = useNavigate();
  const { id } = useParams();
  const {
    control,
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm({ mode: "onChange" });

  const { manageOptionsLanguageId } = useSelector(
    (state) => state.manageOptions,
  );

  const { generalSettings } = useSelector((state) => state.settings);

  const { data: investorPlans, mutate: investorPlansMutate } =
    useGetSubscriptionPlans("INVESTOR", manageOptionsLanguageId);

  const currentPlan = investorPlans?.find((plan) => plan._id === id);

  const status = [
    {
      value: "YES",
      label: "Active",
    },
    {
      value: "NO",
      label: "Inactive",
    },
  ];

  const { trigger: createSubscriptionTrigger, isMutating: createMutating } =
    useCreateInvestorSubscription("INVESTOR", manageOptionsLanguageId);
  const { trigger: updateSubscriptionTrigger, isMutating: updateMutating } =
    useUpdateInvestorSubscription();

  useEffect(() => {
    if (id && currentPlan) {
      reset({
        id: currentPlan?.subscriptionLanguages?._id ?? null,
        title: currentPlan?.subscriptionLanguages?.title ?? "",
        description: currentPlan?.subscriptionLanguages?.description ?? "",
        amount: currentPlan?.amount ?? "",
        cancelStatus: currentPlan?.cancelStatus === "YES" ? true : false,
        isDefault: currentPlan?.isDefault === "YES" ? true : false,
        cancelDays: currentPlan?.cancelDays ?? "",
        refundRequeststatus:
          currentPlan?.refundRequeststatus === "YES" ? true : false,
        refundAllowedDays: currentPlan?.refundAllowedDays ?? "",
        earlyBirdAccessstatus: currentPlan?.earlyBirdAccessstatus ?? "",
        investmentAllowedCount: currentPlan?.investmentAllowedCount ?? "",
        secondaryMarketAccessstatus:
          currentPlan?.secondaryMarketAccessstatus ?? "",
        status: currentPlan?.status ?? "",
      });
    } else if (!id) {
      reset({
        id: null,
        title: "",
        description: "",
        amount: "",
        cancelStatus: false,
        cancelDays: "",
        refundRequeststatus: false,
        refundAllowedDays: "",
        earlyBirdAccessstatus: "",
        investmentAllowedCount: "",
        secondaryMarketAccessstatus: "",
        status: "",
      });
    }
  }, [id, currentPlan, reset]);

  const handleClose = () => {
    navigate("/revenue-settings/investor-fees");
  };

  const onSubmit = async (data) => {
    data.refundRequeststatus = data?.refundRequeststatus ? "YES" : "NO";
    data.cancelStatus = data?.cancelStatus ? "YES" : "NO";
    data.isDefault = data?.isDefault ? "YES" : "NO";
    const res = data?.id
      ? await updateSubscriptionTrigger(data)
      : await createSubscriptionTrigger(data);
    if (res) {
      investorPlansMutate();
      handleClose();
    }
  };

  return (
    <Card className="mt-5">
      <CardHeader>
        <button type="button" onClick={handleClose} className="go-back-btn">
          <i className="fa fa-chevron-left"></i>
        </button>
        <CardTitle className="me-auto">
          {id ? "Edit Subscription" : "Add New Subscription"}
        </CardTitle>
      </CardHeader>
      <CardBody className="pt-0 px-4 pb-6">
        <Row className="justify-content-md-center">
          <Col md={11}>
            <Form
              id="addEditInvestorPlans"
              className="form form-label-right"
              onSubmit={handleSubmit(onSubmit)}>
              <Form.Control type="hidden" name="id" {...register("id")} />
              <div className="form-group floating-input-field-wrapper mb-6">
                <Form.Control
                  placeholder=" "
                  className={classNames("floating-input-field-item", {
                    "is-invalid": errors.title,
                  })}
                  {...register("title", {
                    required: "This field is required",
                  })}
                />
                <label className="floating-input-field-label start-0 ms-4">
                  Title <span className="text-important">*</span>
                </label>
                <ErrorMessage
                  errors={errors}
                  name="title"
                  render={({ message }) => (
                    <div className="invalid-feedback d-block ms-1">
                      {message}
                    </div>
                  )}
                />
              </div>
              <div className="mb-6">
                <div className="form-group floating-input-field-wrapper-textarea">
                  <Form.Control
                    rows={3}
                    as="textarea"
                    placeholder=""
                    className={classNames(
                      "floating-input-field-item form-control",
                      {
                        "is-invalid": errors.description,
                      },
                    )}
                    {...register("description", {
                      required: "This field is required",
                    })}
                  />
                  <label className="floating-input-field-label-static start-0 ms-4">
                    Description <span className="text-important">*</span>
                  </label>
                </div>
                <ErrorMessage
                  errors={errors}
                  name="description"
                  render={({ message }) => (
                    <div className="invalid-feedback d-block ms-1">
                      {message}
                    </div>
                  )}
                />
              </div>
              <div className="row">
                <div className="col-md-6 form-group floating-input-field-wrapper mb-6">
                  <label className="floating-input-field-label-static ms-9">
                    Amount
                    <span className="text-important"> *</span>
                  </label>
                  <FormGroup className="px-4">
                    <Row>
                      <Col
                        xs={"auto"}
                        className="d-flex align-items-center justify-content-center bg-secondary-light rounded-0 rounded-start">
                        <label id="fixedCampaignCommission">
                          {generalSettings.currencyId?.symbol}
                        </label>
                      </Col>
                      <Col className="p-0">
                        <Form.Control
                          name="amount"
                          className={classNames(
                            "rounded-0 rounded-end floating-input-field-item",
                            {
                              "is-invalid": errors.amount,
                            },
                          )}
                          {...register("amount", {
                            required: "This field is required",
                            pattern: {
                              value: /^(\d+(\.\d*)?|\.\d+)$/,
                              message: "Please enter valid value",
                            },
                          })}
                        />
                      </Col>
                    </Row>
                  </FormGroup>
                  <ErrorMessage
                    errors={errors}
                    name="amount"
                    render={({ message }) => (
                      <div className="invalid-feedback d-block ms-1">
                        {message}
                      </div>
                    )}
                  />
                </div>
                <div className="col-md-6 form-group floating-input-field-wrapper mb-6">
                  <Form.Control
                    name="investmentAllowedCount"
                    placeholder=" "
                    className={classNames("floating-input-field-item", {
                      "is-invalid": errors.investmentAllowedCount,
                    })}
                    {...register("investmentAllowedCount", {
                      required: "This field is required",
                      pattern: {
                        value: /^(\d+(\.\d*)?|\.\d+)$/,
                        message: "Please enter valid value",
                      },
                    })}
                  />
                  <label className="floating-input-field-label">
                    No. of Investments Allowed
                    <span className="text-important"> *</span>
                  </label>
                  <ErrorMessage
                    errors={errors}
                    name="investmentAllowedCount"
                    render={({ message }) => (
                      <div className="invalid-feedback d-block ms-1">
                        {message}
                      </div>
                    )}
                  />
                </div>
              </div>
              <div className="row">
                <div className="col-md-6 mb-6">
                  <div className="form-group bg-input-bg-clr p-4 mx-1 rounded-fix d-flex justify-content-between align-items-center">
                    <label className="font-size-sm text-black">
                      Refund Allowed ?
                    </label>
                    <div className="form-group">
                      <Controller
                        name="refundRequeststatus"
                        control={control}
                        defaultValue={false}
                        render={({ field }) => (
                          <Toggle
                            name="refundRequeststatus"
                            icons={{ checked: null, unchecked: null }}
                            checked={field.value}
                            onChange={() => field.onChange(!field.value)}
                          />
                        )}
                      />
                    </div>
                  </div>
                </div>
                {watch("refundRequeststatus") && (
                  <div className="col-md-6 form-group floating-input-field-wrapper mb-6">
                    <Form.Control
                      name="refundAllowedDays"
                      placeholder=" "
                      className={classNames("floating-input-field-item", {
                        "is-invalid": errors.refundAllowedDays,
                      })}
                      {...register("refundAllowedDays", {
                        required: "This field is required",
                        pattern: {
                          value: /^(\d+(\.\d*)?|\.\d+)$/,
                          message: "Please enter valid value",
                        },
                      })}
                    />
                    <label className="floating-input-field-label">
                      Refund Allows Days
                      <span className="text-important"> *</span>
                    </label>
                    <ErrorMessage
                      errors={errors}
                      name="refundAllowedDays"
                      render={({ message }) => (
                        <div className="invalid-feedback d-block ms-1">
                          {message}
                        </div>
                      )}
                    />
                  </div>
                )}
              </div>
              <div className="row">
                <div className="col-md-6 mb-6">
                  <div className="form-group bg-input-bg-clr p-4 mx-1 rounded-fix d-flex justify-content-between align-items-center">
                    <label className="font-size-sm text-black">
                      Cancel Allowed ?
                    </label>
                    <div className="form-group">
                      <Controller
                        name="cancelStatus"
                        control={control}
                        defaultValue={false}
                        render={({ field }) => (
                          <Toggle
                            name="cancelStatus"
                            icons={{ checked: null, unchecked: null }}
                            checked={field.value}
                            onChange={() => field.onChange(!field.value)}
                          />
                        )}
                      />
                    </div>
                  </div>
                </div>
                {watch("cancelStatus") && (
                  <div className="col-md-6 floating-input-field-wrapper form-group mb-6">
                    <Form.Control
                      name="cancelDays"
                      placeholder=" "
                      className={classNames("floating-input-field-item", {
                        "is-invalid": errors.cancelDays,
                      })}
                      {...register("cancelDays", {
                        required: "This field is required",
                        pattern: {
                          value: /^(\d+(\.\d*)?|\.\d+)$/,
                          message: "Please enter valid value",
                        },
                      })}
                    />
                    <label className="floating-input-field-label">
                      Cancel Allows Days
                      <span className="text-important"> *</span>
                    </label>
                    <ErrorMessage
                      errors={errors}
                      name="cancelDays"
                      render={({ message }) => (
                        <div className="invalid-feedback d-block ms-1">
                          {message}
                        </div>
                      )}
                    />
                  </div>
                )}
              </div>
              <div className="row">
                              <div className="col-md-6 mb-6">
                                <div className="form-group bg-input-bg-clr p-4 mx-1 rounded-fix d-flex justify-content-between align-items-center">
                                  <label className="font-size-sm text-black">
                                    Default active plan ?
                                  </label>
                                  <div className="form-group">
                                    <Controller
                                      name="isDefault"
                                      control={control}
                                      defaultValue={false}
                                      render={({ field }) => (
                                        <Toggle
                                          name="isDefault"
                                          icons={{ checked: null, unchecked: null }}
                                          checked={field.value}
                                          onChange={() => field.onChange(!field.value)}
                                        />
                                      )}
                                    />
                                  </div>
                                </div>
                              </div>
                            </div>
              <div className="row">
                <div className="col-md-6 form-group floating-input-field-wrapper mb-6">
                  <label className="floating-input-field-label-static">
                    Secondary Market Access
                    <span className="text-danger"> *</span>
                  </label>
                  <Controller
                    name={"secondaryMarketAccessstatus"}
                    control={control}
                    rules={{ required: "This field is required" }}
                    render={({ field: { value, onChange } }) => {
                      return (
                        <Select
                          placeholder={"Select Status"}
                          classNamePrefix="react-select"
                          className={classNames("react-select-container", {
                            "border border-danger rounded-fix":
                              errors.secondaryMarketAccessstatus,
                          })}
                          options={status}
                          value={status.find((c) => c.value === value)}
                          onChange={(val) => onChange(val.value)}
                        />
                      );
                    }}
                  />
                  <ErrorMessage
                    errors={errors}
                    name="secondaryMarketAccessstatus"
                    render={({ message }) => (
                      <div className="invalid-feedback d-block ms-1">
                        {message}
                      </div>
                    )}
                  />
                </div>
                <div className="col-md-6 form-group floating-input-field-wrapper mb-6">
                  <label className="floating-input-field-label-static">
                    Early Bird Access<span className="text-danger"> *</span>
                  </label>
                  <Controller
                    name={"earlyBirdAccessstatus"}
                    control={control}
                    rules={{ required: "This field is required" }}
                    render={({ field: { value, onChange } }) => {
                      return (
                        <Select
                          placeholder={"Select Status"}
                          classNamePrefix="react-select"
                          className={classNames("react-select-container", {
                            "border border-danger rounded-fix":
                              errors.earlyBirdAccessstatus,
                          })}
                          options={status}
                          value={status.find((c) => c.value === value)}
                          onChange={(val) => onChange(val.value)}
                        />
                      );
                    }}
                  />
                  <ErrorMessage
                    errors={errors}
                    name="earlyBirdAccessstatus"
                    render={({ message }) => (
                      <div className="invalid-feedback d-block ms-1">
                        {message}
                      </div>
                    )}
                  />
                </div>
                <div className="col-12 form-group floating-input-field-wrapper mb-6">
                  <label className="floating-input-field-label-static">
                    Status<span className="text-danger"> *</span>
                  </label>
                  <Controller
                    name={"status"}
                    control={control}
                    rules={{ required: "This field is required" }}
                    render={({ field: { value, onChange } }) => {
                      return (
                        <Select
                          placeholder={"Select Status"}
                          classNamePrefix="react-select"
                          className={classNames("react-select-container", {
                            "border border-danger rounded-fix": errors.status,
                          })}
                          options={status}
                          value={status.find((c) => c.value === value)}
                          onChange={(val) => onChange(val.value)}
                        />
                      );
                    }}
                  />
                  <ErrorMessage
                    errors={errors}
                    name="status"
                    render={({ message }) => (
                      <div className="invalid-feedback d-block ms-1">
                        {message}
                      </div>
                    )}
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={createMutating || updateMutating}
                className="btn btn-secondary common-save-btn mb-2">
                {createMutating || updateMutating ? (
                  <span
                    className="spinner-border spinner-border-sm"
                    role="status"
                    aria-hidden="true"></span>
                ) : (
                  intl.formatMessage({ id: "SAVE" })
                )}
              </button>
            </Form>
          </Col>
        </Row>
      </CardBody>
    </Card>
  );
};
export default compose(withTranslation())(AddEditInvestorSubscription);
